import { Capacitor } from '@capacitor/core'

import { isAppShell } from './platform'

/**
 * Where the signed-in session lives.
 *
 * **On the web: `sessionStorage`, per tab, deliberately.** Two tabs can hold
 * two different people. An instructor signed in beside a student must not have
 * either session reach the other, and a shared store makes that a matter of
 * luck. That decision is load-bearing and is not being changed here.
 *
 * **In the app: `localStorage`.** A WebView has exactly one tab, so per-tab
 * isolation protects nobody from anything — while `sessionStorage` is
 * destroyed the moment Android reclaims the process, which it does routinely
 * to anything in the background. The result was that the app signed you out
 * every time you properly closed it: you were not logged out, the session was
 * simply thrown away with the process.
 *
 * One module rather than a conditional at each call site, because there are
 * five of those and the next one added would quietly use the wrong store.
 */

/**
 * Whether this is a single-tab context, and so wants a persistent store.
 *
 * Deliberately the permissive question — `isAppShell`, any Android WebView —
 * rather than `isOurApp`. Two reasons, and neither is laziness:
 *
 *   The bridge alone was not enough. With a remote `server.url` it is not
 *   reliably injected, so inside the app this answered false, the session went
 *   to sessionStorage, and every cold start signed the user out. That is the
 *   bug this module exists for, and it was reported again after it shipped.
 *
 *   Somebody else's in-app browser is still one tab. Per-tab isolation buys
 *   nothing where there are no other tabs, and a persistent session there is
 *   an improvement, not a risk. Anything that changes what the product *does*
 *   asks `isOurApp` instead — see platform.ts.
 *
 * Wrapped: `Capacitor` is a normal import, and a test that renders something
 * touching auth without mocking it should get the web behaviour rather than an
 * exception thrown from inside a storage read.
 */
function isNative(): boolean {
  try {
    return isAppShell() || Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

function backing(): Storage {
  return isNative() ? window.localStorage : window.sessionStorage
}

export const authStorage = {
  /** True when the session outlives the process — that is, in the app. */
  get persistent(): boolean {
    return isNative()
  },

  get(key: string): string | null {
    // Storage throws rather than returning null when it is unavailable —
    // Safari private mode historically, and a WebView with storage disabled.
    // A signed-out user is a far better outcome than a page that will not
    // render.
    try {
      return backing().getItem(key)
    } catch {
      return null
    }
  },

  set(key: string, value: string): void {
    try {
      backing().setItem(key, value)
    } catch {
      /* Quota or disabled storage. Nothing useful to do; the session simply
         will not survive, which is the old behaviour. */
    }
  },

  remove(key: string): void {
    try {
      backing().removeItem(key)
    } catch {
      /* as above */
    }
  },
}
